package com.dotdrives.client

import com.dotdrives.model.GivingSummary
import com.dotdrives.model.MonthlyGivingSummary
import com.dotdrives.model.OrgBudgetSummary
import com.dotdrives.model.StewardGivingSummary
import com.dotdrives.model.TargetGivingSummary

/**
 * Plain, network-free inputs to [aggregateGivingSummary] -- deliberately not the
 * `@Serializable` row types [GivingSummaryClient] decodes, so this arithmetic is unit-testable
 * (see `GivingSummaryAggregationTest`) without a Supabase client or a fake repository.
 */
data class GivingDonationEntry(
    val targetId: String?,
    val stewardUserId: String?,
    val amount: Double,
    val source: String,
    val year: Int,
    val month: Int,
    val isReversal: Boolean = false,
)

data class GivingTargetEntry(
    val id: String,
    val name: String,
    val goalAmount: Double?,
)

/**
 * Pure aggregation over already-fetched data -- no I/O, so the money math behind the Reports
 * (Summaries) dashboard is unit-tested directly rather than only exercised indirectly through a
 * controller test with a fake repository.
 *
 * [donations] must be *every* donation in the query window, including rows with
 * `targetId == null` (undesignated) and rows whose `targetId` points at a target absent from
 * [targets] (an archived target -- [targets] is always the caller's *active* set). Per-target
 * bars and the goal figure are scoped to [targets], but `orgBudget.raisedAmount` sums every row
 * in [donations] unconditionally, so undesignated and archived-target giving is never silently
 * dropped from a card that presents itself as org-wide.
 *
 * `donations.amount` self-nets correctly under a plain sum: a reversal row
 * (`reverses_donation_id is not null`) carries a negative `amount` by check constraint
 * (docs/schema.md), so no explicit reversal handling is needed here.
 */
fun aggregateGivingSummary(
    targets: List<GivingTargetEntry>,
    donations: List<GivingDonationEntry>,
    stewardNames: Map<String, String>,
    stewardBudgets: Map<String, Double>,
    explicitOrgGoal: Double? = null,
): GivingSummary {
    val byTarget = donations.filter { it.targetId != null }.groupBy { it.targetId }
    val targetSummaries = targets.map { target ->
        TargetGivingSummary(
            targetId = target.id,
            name = target.name,
            raisedAmount = byTarget[target.id].orEmpty().sumOf(GivingDonationEntry::amount),
            goalAmount = target.goalAmount,
        )
    }

    val orgBudget = OrgBudgetSummary(
        raisedAmount = donations.sumOf(GivingDonationEntry::amount),
        goalAmount = explicitOrgGoal,
    )

    val stewards = donations
        .filter { it.stewardUserId != null }
        .groupBy(GivingDonationEntry::stewardUserId)
        .map { (userId, rows) ->
            val id = requireNotNull(userId)
            StewardGivingSummary(
                userId = id,
                name = stewardNames[id] ?: "Unknown",
                raisedAmount = rows.sumOf(GivingDonationEntry::amount),
                budgetAmount = stewardBudgets[id],
            )
        }
        .sortedByDescending(StewardGivingSummary::raisedAmount)

    val months = donations
        .groupBy { it.year to it.month }
        .map { (yearMonth, rows) ->
            val (rowYear, rowMonth) = yearMonth
            // 'subscription' is always an online (recurring) gift -- see the docstring on
            // MonthlyGivingSummary for why it's a subset of onlineAmount, not additive.
            val online = rows.filter { it.source != "manual" }.sumOf(GivingDonationEntry::amount)
            val manual = rows.filter { it.source == "manual" }.sumOf(GivingDonationEntry::amount)
            val recurring = rows.filter { it.source == "subscription" }.sumOf(GivingDonationEntry::amount)
            MonthlyGivingSummary(
                year = rowYear,
                month = rowMonth,
                label = MONTH_NAMES[rowMonth - 1],
                giftCount = rows.count { !it.isReversal && it.amount > 0.0 },
                onlineAmount = online,
                manualAmount = manual,
                recurringAmount = recurring,
            )
        }
        .sortedWith(compareByDescending<MonthlyGivingSummary> { it.year }.thenByDescending { it.month })

    return GivingSummary(targets = targetSummaries, orgBudget = orgBudget, stewards = stewards, months = months)
}

internal val MONTH_NAMES = listOf(
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
)
